import React, { useCallback, useEffect, useRef } from "react";
import CardVideoPlayer from "../../shades/cards/includes/CardVideoPlayer";
import "./AdVideoPlayer.css";

export function isVideoAd(ad) {
  const mediaType = ad?.internalAdType?.toLowerCase();
  if (mediaType === "image" || mediaType === "ads") return false;
  return mediaType === "video" || Boolean(ad?.video?.url || ad?.videoUrl);
}

const previewSource = (src) => src.includes("#") ? src : `${src}#t=0.001`;
const appearanceStyle = (appearance) => ({
  left: `${(appearance?.x ?? 0.5) * 100}%`,
  top: `${(appearance?.y ?? 0.5) * 100}%`,
  transform: `translate(-50%, -50%) scale(${appearance?.scale ?? 1}) rotate(${appearance?.rotation ?? 0}deg)`,
  opacity: appearance?.opacity ?? 1,
});

export default function AdVideoPlayer({ ad, onVideoElement, onPlayRequest, onPause }) {
  const masterRef = useRef(null);
  const backgroundRef = useRef(null);
  const overlayRef = useRef(null);
  const layersPlaying = useRef(false);
  const src = ad.video?.url || ad.videoUrl;
  const composite = ad.adType === "talking_head";
  const attachMaster = useCallback((element) => {
    masterRef.current = element;
    onVideoElement(ad._id, element);
  }, [ad._id, onVideoElement]);

  const startLayers = () => {
    if (layersPlaying.current) return;
    layersPlaying.current = true;
    [backgroundRef.current, overlayRef.current].forEach(layer => {
      try { layer?.play()?.catch(() => {}); } catch { /* The master can still play if an optional layer fails. */ }
    });
  };

  const pauseLayers = () => {
    layersPlaying.current = false;
    [backgroundRef.current, overlayRef.current].forEach(layer => layer?.pause());
  };

  const syncLayers = (master, force = false) => {
    [backgroundRef.current, overlayRef.current].forEach(layer => {
      if (!layer) return;
      const time = layer.loop && Number.isFinite(layer.duration) && layer.duration > 0
        ? master.currentTime % layer.duration
        : master.currentTime;
      if (force || Math.abs(layer.currentTime - time) > 0.25) {
        try { layer.currentTime = time; } catch { /* Wait for layer metadata. */ }
      }
    });
  };

  useEffect(() => {
    const master = masterRef.current;
    const background = backgroundRef.current;
    const overlay = overlayRef.current;
    return () => { master?.pause(); background?.pause(); overlay?.pause(); };
  }, []);

  const composition = composite ? (
    <>
      {ad.backgroundMedia?.url && (
        <div className="ad-video-player__layer" style={appearanceStyle(ad.backgroundMedia.appearance)}>
          {ad.backgroundMedia.type === "video" ? (
            <video ref={backgroundRef} aria-label="Video background" src={previewSource(ad.backgroundMedia.url)} muted loop playsInline preload="metadata" />
          ) : <img src={ad.backgroundMedia.url} alt="Background" />}
        </div>
      )}
      <div className="ad-video-player__layer ad-video-player__avatar" style={appearanceStyle(ad.video?.appearance)}>
        <video ref={overlayRef} aria-label="Talking head layer" src={previewSource(src)} muted playsInline preload="metadata" />
      </div>
    </>
  ) : undefined;

  return (
    <CardVideoPlayer
      key={src}
      src={src}
      title={ad.videoTitle || "Video ad"}
      poster={ad.gifUrl || ad.video?.thumbnail}
      className="ad-video-player__video"
      mediaClassName="ad-video-player__media"
      mediaContent={composition}
      onVideoElement={attachMaster}
      onPlayRequest={(master) => { onPlayRequest(ad._id, master); startLayers(); }}
      onVideoPlay={(master) => { onPlayRequest(ad._id, master); startLayers(); }}
      onVideoPause={() => { pauseLayers(); onPause(ad._id); }}
      onVideoSeek={(master) => syncLayers(master, true)}
      onVideoTimeUpdate={syncLayers}
    />
  );
}
